import { useState, useCallback } from 'react';
import { supabase } from '../clients/supabaseClient';
import { getErrorMessage } from '../utils/errors';

// ── Payment status names that should render as payment cards ──────────────
const PAYMENT_STATUS_NAMES = new Set([
    'AWAITING_PAYMENT',
    'PAYMENT_PRE_AUTHORIZED',
    'PAYMENT_PROCESSING',
    'PAYMENT_SUCCEEDED',
    'PAYMENT_FAILED',
    'PAYMENT_CANCELLED',
    'PAYMENT_DECLINED',
    'PAYMENT_VOIDED',
    'CAPTURE_FAILED',
]);

// ── Timeline entry types ───────────────────────────────────────────────────

export interface OrderTimelineEntry {
    type:       'order';
    id:         string;
    created_at: string;
    statusName: string;
    model_id:   string | null;
    model_type: string | null;
}

export interface PaymentTimelineEntry {
    type:            'payment';
    id:              string;
    created_at:      string;
    statusName:      string;
    model_id:        string | null;
    model_type:      string | null;
    previous_status: string | null;
    current_status:  string | null;
    reason:          string | null;
}

export interface DeliveryTimelineEntry {
    type:       'delivery';
    id:         string;
    created_at: string;
    statusName: string;
    driver_id:  string | null;
}

export type TimelineEntry =
    | OrderTimelineEntry
    | PaymentTimelineEntry
    | DeliveryTimelineEntry;

// ── Hook ──────────────────────────────────────────────────────────────────

interface UseTimelineReturn {
    timeline:      TimelineEntry[];
    loading:       boolean;
    fetchTimeline: (orderId: string) => Promise<void>;
}

interface PaymentHistoryRow {
    id:         string;
    payments_id: string;
    previous:   string | null;
    current:    string | null;
    reason:     string | null;
    created_at: string;
}

interface StatusRow {
    id:   string;
    name: string;
}

export function useTimeline(): UseTimelineReturn {
    const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
    const [loading,  setLoading]  = useState(false);

    const fetchTimeline = useCallback(async (orderId: string) => {
        setLoading(true);
        try {
            // Fetch all sources in parallel.
            const [orderHistRes, paymentHistRes] = await Promise.all([
                // ① order_status_history is the spine — all statuses in order
                supabase
                    .from('order_status_history')
                    .select('id, created_at, model_id, model_type, status:statuses(id, name)')
                    .eq('order_id', orderId)
                    .order('created_at', { ascending: true }),

                // ② payments_history for enrichment — previous → current + reason
                supabase
                    .from('payments_history')
                    .select('id, payments_id, previous, current, reason, created_at')
                    .eq('order_id', orderId)
                    .eq('column', 'status_id')
                    .order('created_at', { ascending: true }),
            ]);

            // ── Resolve payment history UUID → name map ────────────────────
            const paymentRows = (paymentHistRes.data ?? []) as PaymentHistoryRow[];
            const uuids = new Set<string>();
            paymentRows.forEach(row => {
                if (row.previous) uuids.add(row.previous);
                if (row.current)  uuids.add(row.current);
            });

            const statusMap: Record<string, string> = {};
            if (uuids.size > 0) {
                const { data: statuses } = await supabase
                    .from('statuses')
                    .select('id, name')
                    .in('id', Array.from(uuids));
                (statuses ?? [] as StatusRow[]).forEach((s: StatusRow) => { statusMap[s.id] = s.name; });
            }

            // ── Process order_status_history as the spine ──────────────────
            // Match each payment-type spine row to the payments_history row that
            // produced it, by time rather than by status_id. Keying by status_id
            // is wrong: every AWAITING_PAYMENT spine row (from every payment
            // attempt on the order) would collapse onto whichever payments_history
            // row last happened to have current = AWAITING_PAYMENT, regardless of
            // which attempt actually produced that spine row.
            //
            // Instead we exploit the fact that every reconcile path writes its
            // payments_history row strictly before the order_status_history row
            // it corresponds to (see reconcilePreAuth / reconcileImmediateDebit /
            // the webhook handler — payments_history insert, then order update +
            // order_status_history insert). Since both arrays are sorted
            // ascending by created_at, a simple forward-only pointer finds the
            // latest not-yet-consumed payments_history row at or before each
            // spine row's timestamp — a stable one-to-one temporal match.
            let payIdx = 0;
            const entries: TimelineEntry[] = [];

            for (const row of (orderHistRes.data ?? [])) {
                const statusRaw   = row.status as unknown;
                const statusEntry = (Array.isArray(statusRaw) ? statusRaw[0] : statusRaw) as { id: string; name: string } | null;
                const statusName  = statusEntry?.name ?? '';

                if (PAYMENT_STATUS_NAMES.has(statusName)) {
                    // Advance past every payments_history row at or before this
                    // spine row's timestamp, keeping the last (nearest) one.
                    let matchedRow: PaymentHistoryRow | null = null;
                    while (payIdx < paymentRows.length && paymentRows[payIdx].created_at <= row.created_at) {
                        matchedRow = paymentRows[payIdx];
                        payIdx++;
                    }
                    const enrichment = matchedRow ? {
                        previous_status: matchedRow.previous ? (statusMap[matchedRow.previous] ?? null) : null,
                        current_status:  matchedRow.current  ? (statusMap[matchedRow.current]  ?? null) : null,
                        reason:          matchedRow.reason ?? null,
                    } : null;
                    entries.push({
                        type:            'payment',
                        id:              row.id,
                        created_at:      row.created_at,
                        statusName,
                        model_id:        row.model_id ?? null,
                        model_type:      row.model_type ?? null,
                        previous_status: enrichment?.previous_status ?? null,
                        current_status:  enrichment?.current_status ?? statusName,
                        reason:          enrichment?.reason ?? null,
                    });
                } else if (
                    statusName === 'DRIVER_ASSIGNED' ||
                    statusName === 'ASSIGN_DRIVER'   ||
                    statusName === 'ON_ROUTE'         ||
                    statusName === 'DELIVERY_COMPLETE'||
                    statusName === 'DELIVERY_FAILED'
                ) {
                    // Delivery-phase entries — use model_id from order_status_history
                    // as the driver reference (stamped by sync_delivery_status_to_orders
                    // and our backfill for unassign).
                    entries.push({
                        type:        'delivery',
                        id:          row.id,
                        created_at:  row.created_at,
                        statusName,
                        driver_id:   row.model_id ?? null,
                    });
                } else {
                    entries.push({
                        type:       'order',
                        id:         row.id,
                        created_at: row.created_at,
                        statusName,
                        model_id:   row.model_id ?? null,
                        model_type: row.model_type ?? null,
                    });
                }
            }

            // order_status_history is already sorted ascending — no re-sort needed.
            setTimeline(entries);
        } catch (err) {
            console.error('useTimeline.fetchTimeline:', getErrorMessage(err, 'Failed to load timeline'));
        } finally {
            setLoading(false);
        }
    }, []);

    return { timeline, loading, fetchTimeline };
}